<script setup>
/**
 * OCHAT: tipos de habitación y sus planes de tarifa.
 *
 * QUÉ SE VENDE Y CÓMO SE VENDE, EN DOS NIVELES
 * El tipo de habitación es LO QUE se vende: el huésped compra "una doble", no la
 * habitación 214. El plan de tarifa es CÓMO se vende esa misma doble: flexible más
 * cara con cancelación gratis, no reembolsable más barata, con desayuno. Por eso los
 * planes cuelgan del tipo y no del hotel, y por eso están anidados acá.
 *
 * El motor de disponibilidad devuelve una oferta por plan activo, así que agregar un
 * plan acá aparece solo en la búsqueda y en lo que el agente IA ofrece por WhatsApp.
 *
 * BORRAR UN PLAN CON EL QUE YA SE VENDIÓ NO LO BORRA
 * El servidor lo desactiva, porque las reservas apuntan al plan y su nombre aparece
 * en el historial. La pantalla dice cuál de las dos cosas pasó en lugar de mentir con
 * un "eliminado".
 *
 * La confirmación de borrado es en el lugar y no un modal: son acciones de
 * configuración poco frecuentes, y un modal por cada una sería más ruido que ayuda.
 */
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  property: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const uiFlags = useMapGetter('bookingSettings/getUIFlags');

const MEAL_PLANS = [
  'room_only',
  'breakfast',
  'half_board',
  'full_board',
  'all_inclusive',
];

// --------------------------------------------------------- tipos de habitación

const blankRoomType = () => ({
  code: '',
  name: '',
  description: '',
  max_adults: 2,
  max_children: 0,
  base_occupancy: 2,
});

const roomTypeDraft = ref(null); // null = ningún formulario abierto
const editingRoomTypeId = ref(null);
const confirmingRoomTypeId = ref(null);

const openNewRoomType = () => {
  editingRoomTypeId.value = null;
  roomTypeDraft.value = blankRoomType();
};

const openEditRoomType = roomType => {
  editingRoomTypeId.value = roomType.id;
  roomTypeDraft.value = {
    code: roomType.code,
    name: roomType.name,
    description: roomType.description ?? '',
    max_adults: roomType.max_adults,
    max_children: roomType.max_children,
    base_occupancy: roomType.base_occupancy,
  };
};

const closeRoomTypeForm = () => {
  roomTypeDraft.value = null;
  editingRoomTypeId.value = null;
};

const saveRoomType = async () => {
  try {
    const payload = { propertyId: props.property.id, ...roomTypeDraft.value };
    if (editingRoomTypeId.value) {
      await store.dispatch('bookingSettings/updateRoomType', {
        ...payload,
        id: editingRoomTypeId.value,
      });
    } else {
      await store.dispatch('bookingSettings/createRoomType', payload);
    }
    closeRoomTypeForm();
    useAlert(t('BOOKINGS.SETTINGS.ROOM_TYPE.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('BOOKINGS.SETTINGS.ROOM_TYPE.FAILED')
    );
  }
};

const deleteRoomType = async id => {
  try {
    await store.dispatch('bookingSettings/deleteRoomType', {
      propertyId: props.property.id,
      id,
    });
    confirmingRoomTypeId.value = null;
    useAlert(t('BOOKINGS.SETTINGS.ROOM_TYPE.DELETED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('BOOKINGS.SETTINGS.ROOM_TYPE.DELETE_FAILED')
    );
  }
};

// ------------------------------------------------------------ planes de tarifa

const blankRatePlan = () => ({
  code: '',
  name: '',
  refundable: true,
  cancellation_hours: 24,
  meal_plan: 'room_only',
  active: true,
});

const ratePlanDraft = ref(null);
const ratePlanRoomTypeId = ref(null);
const editingRatePlanId = ref(null);
const confirmingRatePlanId = ref(null);

const openNewRatePlan = roomTypeId => {
  editingRatePlanId.value = null;
  ratePlanRoomTypeId.value = roomTypeId;
  ratePlanDraft.value = blankRatePlan();
};

const openEditRatePlan = (roomTypeId, plan) => {
  editingRatePlanId.value = plan.id;
  ratePlanRoomTypeId.value = roomTypeId;
  ratePlanDraft.value = {
    code: plan.code,
    name: plan.name,
    refundable: plan.refundable,
    cancellation_hours: plan.cancellation_hours,
    meal_plan: plan.meal_plan,
    active: plan.active,
  };
};

const closeRatePlanForm = () => {
  ratePlanDraft.value = null;
  ratePlanRoomTypeId.value = null;
  editingRatePlanId.value = null;
};

const saveRatePlan = async () => {
  try {
    const payload = {
      propertyId: props.property.id,
      roomTypeId: ratePlanRoomTypeId.value,
      ...ratePlanDraft.value,
    };
    if (editingRatePlanId.value) {
      await store.dispatch('bookingSettings/updateRatePlan', {
        ...payload,
        id: editingRatePlanId.value,
      });
    } else {
      await store.dispatch('bookingSettings/createRatePlan', payload);
    }
    closeRatePlanForm();
    useAlert(t('BOOKINGS.SETTINGS.RATE_PLAN.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('BOOKINGS.SETTINGS.RATE_PLAN.FAILED')
    );
  }
};

const deleteRatePlan = async (roomTypeId, id) => {
  try {
    const result = await store.dispatch('bookingSettings/deleteRatePlan', {
      propertyId: props.property.id,
      roomTypeId,
      id,
    });
    confirmingRatePlanId.value = null;
    // Con cuerpo en la respuesta el plan sobrevivió desactivado: ya se vendió con él.
    useAlert(
      result?.id
        ? t('BOOKINGS.SETTINGS.RATE_PLAN.DEACTIVATED')
        : t('BOOKINGS.SETTINGS.RATE_PLAN.DELETED')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('BOOKINGS.SETTINGS.RATE_PLAN.DELETE_FAILED')
    );
  }
};

const isBusy = () =>
  uiFlags.value.isCreating ||
  uiFlags.value.isUpdating ||
  uiFlags.value.isDeleting;
</script>

<template>
  <div class="flex flex-col gap-4">
    <p v-if="!property.room_types?.length" class="text-sm text-n-slate-11">
      {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.EMPTY') }}
    </p>

    <article
      v-for="roomType in property.room_types"
      :key="roomType.id"
      class="flex flex-col gap-3 p-4 rounded-lg outline outline-1 outline-n-container"
    >
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ roomType.name }}
            <span class="font-normal text-n-slate-11">{{ roomType.code }}</span>
          </h4>
          <p class="text-sm text-n-slate-11">
            {{
              $t('BOOKINGS.SETTINGS.ROOM_TYPE.SUMMARY', {
                adults: roomType.max_adults,
                children: roomType.max_children,
                base: roomType.base_occupancy,
              })
            }}
          </p>
          <!--
            Solo si hay alguna. Un "0 habitaciones físicas" se lee como un dato que
            falta, cuando en realidad cargar las habitaciones una por una es opcional
            y no hace falta para vender: el cupo de cada noche es un número aparte.
          -->
          <p
            v-if="roomType.physical_room_count"
            class="text-sm text-n-slate-11"
          >
            {{
              $t('BOOKINGS.SETTINGS.ROOM_TYPE.PHYSICAL', {
                n: roomType.physical_room_count,
              })
            }}
          </p>
        </div>

        <div class="flex items-center gap-2 text-sm">
          <button
            type="button"
            class="text-n-brand hover:underline"
            @click="openEditRoomType(roomType)"
          >
            {{ $t('BOOKINGS.SETTINGS.EDIT') }}
          </button>

          <template v-if="confirmingRoomTypeId === roomType.id">
            <span class="text-n-slate-11">
              {{ $t('BOOKINGS.SETTINGS.CONFIRM') }}
            </span>
            <button
              type="button"
              :disabled="isBusy()"
              class="font-medium text-n-ruby-11 hover:underline disabled:opacity-50"
              @click="deleteRoomType(roomType.id)"
            >
              {{ $t('BOOKINGS.SETTINGS.CONFIRM_YES') }}
            </button>
            <button
              type="button"
              class="text-n-slate-11 hover:underline"
              @click="confirmingRoomTypeId = null"
            >
              {{ $t('BOOKINGS.SETTINGS.CONFIRM_NO') }}
            </button>
          </template>
          <button
            v-else
            type="button"
            class="text-n-ruby-11 hover:underline"
            @click="confirmingRoomTypeId = roomType.id"
          >
            {{ $t('BOOKINGS.SETTINGS.DELETE') }}
          </button>
        </div>
      </header>

      <p v-if="roomType.description" class="text-sm text-n-slate-11">
        {{ roomType.description }}
      </p>

      <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h5 class="text-sm font-medium text-n-slate-12">
            {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.TITLE') }}
          </h5>
          <button
            type="button"
            class="text-sm text-n-brand hover:underline"
            @click="openNewRatePlan(roomType.id)"
          >
            {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.ADD') }}
          </button>
        </div>

        <p v-if="!roomType.rate_plans?.length" class="text-sm text-n-amber-11">
          {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.EMPTY') }}
        </p>

        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="plan in roomType.rate_plans"
            :key="plan.id"
            class="flex flex-wrap items-center justify-between gap-2 text-sm"
          >
            <span class="flex flex-wrap items-center gap-2">
              <span class="text-n-slate-12">{{ plan.name }}</span>
              <span class="text-n-slate-11">{{ plan.code }}</span>
              <span class="text-n-slate-11">
                {{
                  $t(
                    `BOOKINGS.SETTINGS.MEAL_PLAN.${plan.meal_plan.toUpperCase()}`
                  )
                }}
              </span>
              <span class="text-n-slate-11">
                {{
                  plan.refundable
                    ? $t('BOOKINGS.SETTINGS.RATE_PLAN.REFUNDABLE_UNTIL', {
                        n: plan.cancellation_hours,
                      })
                    : $t('BOOKINGS.NON_REFUNDABLE')
                }}
              </span>
              <span v-if="!plan.active" class="text-n-amber-11">
                {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.INACTIVE') }}
              </span>
            </span>

            <span class="flex items-center gap-2">
              <button
                type="button"
                class="text-n-brand hover:underline"
                @click="openEditRatePlan(roomType.id, plan)"
              >
                {{ $t('BOOKINGS.SETTINGS.EDIT') }}
              </button>

              <template v-if="confirmingRatePlanId === plan.id">
                <span class="text-n-slate-11">
                  {{ $t('BOOKINGS.SETTINGS.CONFIRM') }}
                </span>
                <button
                  type="button"
                  :disabled="isBusy()"
                  class="font-medium text-n-ruby-11 hover:underline disabled:opacity-50"
                  @click="deleteRatePlan(roomType.id, plan.id)"
                >
                  {{ $t('BOOKINGS.SETTINGS.CONFIRM_YES') }}
                </button>
                <button
                  type="button"
                  class="text-n-slate-11 hover:underline"
                  @click="confirmingRatePlanId = null"
                >
                  {{ $t('BOOKINGS.SETTINGS.CONFIRM_NO') }}
                </button>
              </template>
              <button
                v-else
                type="button"
                class="text-n-ruby-11 hover:underline"
                @click="confirmingRatePlanId = plan.id"
              >
                {{ $t('BOOKINGS.SETTINGS.DELETE') }}
              </button>
            </span>
          </li>
        </ul>

        <form
          v-if="ratePlanDraft && ratePlanRoomTypeId === roomType.id"
          class="flex flex-col gap-3 p-3 mt-1 rounded-lg bg-n-alpha-1"
          @submit.prevent="saveRatePlan"
        >
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.NAME') }}
              <input
                v-model="ratePlanDraft.name"
                type="text"
                required
                class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.CODE') }}
              <input
                v-model="ratePlanDraft.code"
                type="text"
                required
                class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.MEAL_PLAN') }}
              <select
                v-model="ratePlanDraft.meal_plan"
                class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
              >
                <option v-for="meal in MEAL_PLANS" :key="meal" :value="meal">
                  {{ $t(`BOOKINGS.SETTINGS.MEAL_PLAN.${meal.toUpperCase()}`) }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.CANCELLATION_HOURS') }}
              <input
                v-model.number="ratePlanDraft.cancellation_hours"
                type="number"
                min="0"
                step="1"
                :disabled="!ratePlanDraft.refundable"
                class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12 disabled:opacity-50"
              />
            </label>
          </div>

          <div class="flex flex-wrap gap-4 text-sm text-n-slate-11">
            <label class="flex items-center gap-2">
              <input v-model="ratePlanDraft.refundable" type="checkbox" />
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.IS_REFUNDABLE') }}
            </label>
            <label class="flex items-center gap-2">
              <input v-model="ratePlanDraft.active" type="checkbox" />
              {{ $t('BOOKINGS.SETTINGS.RATE_PLAN.IS_ACTIVE') }}
            </label>
          </div>

          <div class="flex items-center gap-3">
            <button
              type="submit"
              :disabled="isBusy()"
              class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
            >
              {{ $t('BOOKINGS.SETTINGS.SAVE') }}
            </button>
            <button
              type="button"
              class="text-sm text-n-slate-11 hover:underline"
              @click="closeRatePlanForm"
            >
              {{ $t('BOOKINGS.SETTINGS.CANCEL') }}
            </button>
          </div>
        </form>
      </div>
    </article>

    <form
      v-if="roomTypeDraft"
      class="flex flex-col gap-3 p-4 rounded-lg outline outline-1 outline-n-brand"
      @submit.prevent="saveRoomType"
    >
      <h4 class="text-sm font-medium text-n-slate-12">
        {{
          editingRoomTypeId
            ? $t('BOOKINGS.SETTINGS.ROOM_TYPE.EDIT_TITLE')
            : $t('BOOKINGS.SETTINGS.ROOM_TYPE.NEW_TITLE')
        }}
      </h4>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.NAME') }}
          <input
            v-model="roomTypeDraft.name"
            type="text"
            required
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.CODE') }}
          <input
            v-model="roomTypeDraft.code"
            type="text"
            required
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.DESCRIPTION') }}
        <input
          v-model="roomTypeDraft.description"
          type="text"
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        />
      </label>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.MAX_ADULTS') }}
          <input
            v-model.number="roomTypeDraft.max_adults"
            type="number"
            min="1"
            step="1"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.MAX_CHILDREN') }}
          <input
            v-model.number="roomTypeDraft.max_children"
            type="number"
            min="0"
            step="1"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.BASE_OCCUPANCY') }}
          <input
            v-model.number="roomTypeDraft.base_occupancy"
            type="number"
            min="1"
            step="1"
            class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
          />
          <span class="text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.BASE_OCCUPANCY_HINT') }}
          </span>
        </label>
      </div>

      <div class="flex items-center gap-3">
        <button
          type="submit"
          :disabled="isBusy()"
          class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
        >
          {{ $t('BOOKINGS.SETTINGS.SAVE') }}
        </button>
        <button
          type="button"
          class="text-sm text-n-slate-11 hover:underline"
          @click="closeRoomTypeForm"
        >
          {{ $t('BOOKINGS.SETTINGS.CANCEL') }}
        </button>
      </div>
    </form>

    <div v-else>
      <button
        type="button"
        class="text-sm text-n-brand hover:underline"
        @click="openNewRoomType"
      >
        {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.ADD') }}
      </button>
    </div>
  </div>
</template>
